import {
  x0Accessor,
  x1Accessor,
  xAccessor,
  y0Accessor,
  y1Accessor,
  yAccessor,
} from './area-accessors.js';
import {
  bandX0Accessor,
  bandX1Accessor,
  bandY0Accessor,
  bandY1Accessor,
} from './band-accessors.js';
import { AreaCanvas } from '../../../core/components/canvas/components/AreaCanvas.js';
import { shouldDisplayPoints } from '../../../core/components/canvas/utils/display-points.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { POINT_OVERLAY_PADDING } from '../../../core/components/data-point/constants.js';
import { DEFAULT_FILL_OPACITY } from '../../../core/constants.js';
import { isIsolatedPoint } from '../../../core/utils/is-isolated-point.js';
import { DEFAULT_POINTS_DISPLAY } from '../../constants/defaults.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  AreaMetadata,
  AreaSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import type { AreaDatapoint } from '../../types/xy-chart.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { isGapDatapointForDisplay } from '../../utils/validation-datapoint.js';
import { relAccessor } from '../../utils/value-representation.js';

interface AreaDisplayProps {
  axisIdToScales: AxisIdToScales;
  series: AreaSeriesWithDatapoints;
  metadata: AreaMetadata;
}

export function AreaDisplay({
  axisIdToScales,
  series,
  metadata,
}: Readonly<AreaDisplayProps>) {
  const { height, width } = useGridPlotSize();
  const { datapointMetadataMap, axisIdToScaleDomain } = useXYChartData();

  const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
  const {
    curve,
    color,
    orientation,
    variant,
    pointsDisplay,
    xAxis,
    yAxis,
    isStacked,
  } = metadata;

  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const showBandedArea = variant === 'band';

  const definedAccessor = isGapDatapointForDisplay(datapointMetadataMap);

  const showDataPoints =
    variant === 'band'
      ? pointsDisplay === 'always'
      : shouldDisplayPoints(
          pointsDisplay ?? DEFAULT_POINTS_DISPLAY,
          width,
          series.data.length,
        );

  const isolatedAccessor = (datapoints: AreaDatapoint[], index: number) =>
    showDataPoints ||
    isIsolatedPoint(datapoints, index, (point) =>
      isGapDatapointForDisplay(datapointMetadataMap)(point),
    );

  const isHorizontal = orientation === 'horizontal';

  let primaryAxisAccessor;
  let baselineAccessor;
  let endpointAccessor;

  if (variant === 'band') {
    primaryAxisAccessor = isHorizontal
      ? bandY0Accessor(yScale, yRelAccessor)
      : bandX0Accessor(xScale, xRelAccessor);
    baselineAccessor = isHorizontal
      ? bandX0Accessor(xScale, xRelAccessor)
      : bandY0Accessor(yScale, yRelAccessor);
    endpointAccessor = isHorizontal
      ? bandX1Accessor(xScale, xRelAccessor)
      : bandY1Accessor(yScale, yRelAccessor);
  } else {
    primaryAxisAccessor = isHorizontal
      ? yAccessor(yScale, yRelAccessor)
      : xAccessor(xScale, xRelAccessor);
    baselineAccessor = isHorizontal
      ? x0Accessor(xScale, datapointMetadataMap, xRelAccessor, isStacked)
      : y0Accessor(yScale, datapointMetadataMap, yRelAccessor, isStacked);
    endpointAccessor = isHorizontal
      ? x1Accessor(xScale, datapointMetadataMap, xRelAccessor, isStacked)
      : y1Accessor(yScale, datapointMetadataMap, yRelAccessor, isStacked);
  }

  return (
    <AreaCanvas<AreaDatapoint>
      key={series.id}
      data={series.data}
      canvasWidth={width}
      canvasHeight={height}
      opacity={DEFAULT_FILL_OPACITY}
      color={() => color.Default}
      x={primaryAxisAccessor}
      y0={baselineAccessor}
      y1={endpointAccessor}
      defined={definedAccessor}
      displayScatterPlot={isolatedAccessor}
      enableDotOverflow
      overlayPadding={POINT_OVERLAY_PADDING}
      orientation={orientation}
      curve={curve}
      showBandedArea={showBandedArea}
    />
  );
}

AreaDisplay['displayName'] = 'AreaDisplay';
